import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import {
  getSingleAdminRecipe,
  deleteRecipe,
  resetForm,
  updateRecipe,
} from '../../actions/adminRecipes';
import { Helmet } from 'react-helmet';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import RecipeForm from './RecipeForm';

const UpdateRecipe = ({
  adminRecipes,
  getSingleAdminRecipe,
  deleteRecipe,
  resetForm,
  updateRecipe,
}: any) => {
  const history = useHistory();
  const location = useLocation();
  const params = useParams() as any;
  const ar: any = adminRecipes;

  const [values, setValues] = useState({
    title: '',
    description: '',
    prepTime: '',
    totalTime: '',
    caloriesPerServing: '',
    servings: '',
  });
  const [recipeImage, setRecipeImage] = useState<any>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [ingredients, setIngredients] = useState(['']);
  const [directions, setDirections] = useState(['']);
  const [errors, setErrors] = useState<any>({});
  const [recipe, setRecipe] = useState<any>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showDeletedMsg, setShowDeletedMsg] = useState(false);

  const populateForm = (r: any) => {
    setValues({
      title: r.title,
      description: r.description,
      prepTime: r.prepTime,
      totalTime: r.totalTime,
      caloriesPerServing: r.caloriesPerServing,
      servings: r.servings,
    });
    setImageUrl(r.recipeImage);
    setIngredients(r.ingredients.map((i: any) => i.step));
    setDirections(r.directions.map((d: any) => d.direction));
    setRecipe(r);
  };

  useEffect(() => {
    const ls = location.state as any;
    if (ls?.recipe) {
      populateForm(ls.recipe);
    } else {
      getSingleAdminRecipe(params.recipeid);
    }
    return () => { resetForm(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setErrors(ar.errors);
  }, [ar.errors]);

  useEffect(() => {
    const r = ar.recipe;
    if (!r || !r.title) return;
    populateForm(r);
  }, [ar.recipe]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (ar.recipeUpdated) {
      setShowSuccess(true);
      document.querySelector('.admin-main-inner')?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [ar.recipeUpdated]);

  useEffect(() => {
    if (ar.recipeDeleted) {
      setShowDeletedMsg(true);
      setTimeout(() => history.push('/adminpanel/recipes'), 3000);
    }
  }, [ar.recipeDeleted]); // eslint-disable-line react-hooks/exhaustive-deps

  const onChange = (e: any) => {
    const { name, value } = e.target;
    const newErrors = { ...errors };
    if (newErrors[name]) delete newErrors[name];
    let parsed: any = value;
    if ((name === 'caloriesPerServing' || name === 'servings') && value !== '') parsed = parseFloat(value);
    setValues((prev) => ({ ...prev, [name]: parsed }));
    setErrors(newErrors);
    if (ar.recipeUpdated) resetForm();
    setShowSuccess(false);
  };

  const onImageSelect = (e: any) => {
    if (e.target.name !== 'recipeImage') return;
    const newErrors = { ...errors };
    delete newErrors.recipeImage;
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      setRecipeImage(file);
      setImageUrl(reader.result as string);
      setErrors(newErrors);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    const formData = new FormData();
    if (recipeImage) formData.append('recipeImage', recipeImage);
    if (values.title) formData.append('title', values.title);
    if (values.description) formData.append('description', values.description);
    if (values.prepTime) formData.append('prepTime', values.prepTime);
    if (values.totalTime) formData.append('totalTime', values.totalTime);
    if (values.caloriesPerServing) formData.append('caloriesPerServing', String(values.caloriesPerServing));
    if (values.servings) formData.append('servings', String(values.servings));
    formData.append('ingredients', JSON.stringify(ingredients));
    formData.append('directions', JSON.stringify(directions));
    updateRecipe(formData, recipe._id);
  };

  const locationState = location.state as any;
  const pageTitle = locationState?.recipe
    ? locationState.recipe.title
    : params.title?.replace(/-/g, ' ') ?? '';

  return (
    <>
      <Helmet>
        <title>{pageTitle} - Frozberries Victoria</title>
      </Helmet>

      <div className="admin-card" style={{ marginBottom: 0 }}>
        <div className="admin-card-header">
          <p className="admin-card-title">{pageTitle}</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {showSuccess && (
              <>
                <span className="status-badge confirmed" style={{ fontSize: '0.78rem' }}>Updated!</span>
                {ar.recipe && (
                  <span
                    style={{ fontSize: '0.78rem', color: '#ec174f', cursor: 'pointer' }}
                    onClick={() =>
                      history.push(`/recipes/${ar.recipe.title.replace(/[^A-Z0-9]+/gi, '-')}/${ar.recipe._id}`)
                    }
                  >
                    View recipe →
                  </span>
                )}
              </>
            )}
            {showDeletedMsg && (
              <span style={{ fontSize: '0.78rem', color: '#6d7175' }}>Deleted. Redirecting…</span>
            )}
          </div>
        </div>
      </div>

      <RecipeForm
        values={values}
        errors={errors}
        imageUrl={imageUrl}
        ingredients={ingredients}
        directions={directions}
        loading={ar.loading}
        submitLabel="Update Recipe"
        onChange={onChange}
        onImageSelect={onImageSelect}
        onSubmit={handleSubmit}
        onBack={() => history.push('/adminpanel/recipes')}
        onIngredientsChange={setIngredients}
        onDirectionsChange={setDirections}
        extraActions={
          !confirmDelete ? (
            <button
              type="button"
              className="admin-dropdown-trigger"
              style={{ background: '#f8d7da', color: '#721c24', borderColor: '#f8d7da' }}
              onClick={() => setConfirmDelete(true)}
            >
              Delete Recipe
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.78rem', color: '#6d7175' }}>Sure?</span>
              <button
                type="button"
                className="admin-dropdown-trigger"
                style={{ background: '#f8d7da', color: '#721c24', borderColor: '#f8d7da' }}
                onClick={() => deleteRecipe(recipe._id)}
              >
                {ar.deleteButtonLoading ? 'Deleting…' : 'Yes, delete'}
              </button>
              <button
                type="button"
                className="admin-dropdown-trigger"
                onClick={() => setConfirmDelete(false)}
              >
                Cancel
              </button>
            </div>
          )
        }
      />
    </>
  );
};

const mapStateToProps = (state: any) => ({
  adminRecipes: state.adminRecipes,
});

export default connect(mapStateToProps, {
  getSingleAdminRecipe,
  deleteRecipe,
  resetForm,
  updateRecipe,
})(UpdateRecipe);
